import { useEffect, useRef, useState } from 'react';
import { App } from '../App';
import { AuthScreen } from '../ui/AuthScreen';
import { authClient } from './client';

/**
 * Session boundary of the whole client.
 *
 * The game (and therefore Phaser) mounts ONLY under a confirmed session:
 * no anonymous access, no query-string bypass, no auto-account. Refreshing
 * the page re-validates the cookie against the API; signing out unmounts the
 * arena and returns to the entry screen.
 *
 * The dev-tools gate is unchanged and orthogonal: debug flags still require
 * a build that allows them, and none of them touch authentication.
 */
export function AuthGate() {
  const { data: session, isPending, error, refetch } = authClient.useSession();
  // Distinguishes "never signed in" from "session vanished mid-play", so the
  // entry screen can say the session expired instead of a bare login form.
  const hadSession = useRef(false);
  const [signingOut, setSigningOut] = useState(false);

  useEffect(() => {
    if (session) {
      hadSession.current = true;
    }
  }, [session]);

  if (isPending) {
    return (
      <div className="auth-shell" data-testid="auth-loading">
        <p className="auth-status">Verificando sessão…</p>
      </div>
    );
  }

  if (error && !session) {
    // Network failure or 5xx while checking the session: the API or the
    // database is unavailable. Clear screen, retry, no internals leaked.
    return (
      <div className="auth-shell" data-testid="auth-offline">
        <div className="auth-panel">
          <h1 className="auth-title">KENOCYBER</h1>
          <p className="auth-error" data-testid="auth-offline-message">
            Servidor indisponível no momento. Tente novamente em instantes.
          </p>
          <button type="button" className="auth-submit" onClick={() => refetch()}>
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <AuthScreen
        sessionExpired={hadSession.current}
        onAuthenticated={() => {
          refetch();
        }}
      />
    );
  }

  const signOut = async () => {
    if (signingOut) {
      return;
    }
    setSigningOut(true);
    try {
      // Revokes the session server-side and clears the cookie; useSession
      // then flips to null and the arena unmounts.
      await authClient.signOut();
    } finally {
      setSigningOut(false);
    }
  };

  /*
   * The id as well as the name, and they answer different questions.
   *
   * The name is what a player is CALLED - it is shown, and it can change. The
   * id is who they ARE. Anything that must not survive a change of account
   * keys on the id: a duel's socket, its pause lease and its in-flight
   * challenge all belong to one player and must never reach the next.
   *
   * Both come out of the session that is already loaded. No extra request.
   */
  return <App playerName={session.user.name} accountId={session.user.id} onSignOut={signOut} />;
}
